import "./style.scss";
import { FC, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import { AOPMaltaInvoiceData, AOPPolandInvoiceData, AOPUkraineInvoiceData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPDegreeSelect from "@/src/components/AdmissionsOfficePage/shared/AOPDegreeSelect/AOPDegreeSelect";
import AOPTaskIdInput from "@/src/components/AdmissionsOfficePage/shared/AOPTaskIdInput/AOPTaskIdInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { INVOICE_LETTER_BASIC_REGISTRY } from "../invoiceLetterBasicRegistry";
import type { AdmissionOfficeCountry } from "../../../admissionOfficeCountry";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import { useSyncWithQRPreview } from "@/src/hooks/useSyncWithQRPreview";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import { joinSelectedCourse, splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  selectedCourse: string;
  degree?: string;
  mediumOfInstruction: string;
  duration: string;
  invoiceBody: string;
  signature: string;
  coursePrefix: string;
  task_id?: string;
  withQR?: boolean;
}

interface AOPConfigProps {
  country: AdmissionOfficeCountry;
  dataForGeneration: AOPMaltaInvoiceData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaInvoiceData>>;
  setWithQRForPreview: React.Dispatch<React.SetStateAction<boolean>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev, setWithQRForPreview }) => {
  const registryEntry = INVOICE_LETTER_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      invoiceBody: "",
      currentDate: formatDateForInput(today),
      withQR: true,
    },
  });

  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);

  const generatedDoc = useAdmissionGeneratedDocumentActions({ getValues, documentType: "invoice" });

  const course = watch("course");
  const invoiceBody = watch("invoiceBody");
  const withQR = watch("withQR");

  useSyncWithQRPreview(withQR, setWithQRForPreview);

  // AUTOCOMPLETE ↓
  const signatureAuto = useAutocomplete(Object.keys(registryEntry.signatures));
  const offeredCourseAuto = useAutocomplete(Object.keys(registryEntry.admissionCourses));
  const invoiceBodyAuto = useAutocomplete(Object.keys(registryEntry.invoiceCourses));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "invoice",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto);
    },
  });

  // EDITORS
  useEffect(() => {
    if (!course) {
      setContent("");
      return;
    }
    const courseSettings = INVOICE_LETTER_BASIC_REGISTRY[country].admissionCourses[course];
    if (courseSettings) {
      setContent(courseSettings.tinyMCEContent);
      const { degree, selectedCourse } = splitSelectedCourse(courseSettings.selected_course);
      setValue("degree", degree);
      setValue("selectedCourse", selectedCourse);
      setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
      setValue("duration", courseSettings.duration);
      setTitle(courseSettings.title);
    } else {
      setContent("");
    }
  }, [course, setValue, country]);

  useEffect(() => {
    if (!invoiceBody) {
      setInvoiceContent("");
      return;
    }
    const invoiceSettings = INVOICE_LETTER_BASIC_REGISTRY[country].invoiceCourses[invoiceBody];
    setInvoiceContent(invoiceSettings ? invoiceSettings.tinyMCEContent : "");
  }, [invoiceBody, country]);

  const generateNewDocument = async (_values: IAOPFormValues) => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;

      let response;
      if (country === "malta") {
        response = await admissionOfficeRequests.createInvoiceDoc(dataForGeneration);
      } else if (country === "poland") {
        response = await admissionOfficeRequests.createPolandInvoiceDoc(dataForGeneration as AOPPolandInvoiceData);
      } else {
        response = await admissionOfficeRequests.createUkraineInvoiceDoc(dataForGeneration as AOPUkraineInvoiceData);
      }
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      coursePrefix: watch("coursePrefix"),
      selectedCourse: watch("selectedCourse"),
      degree: watch("degree"),
      duration: watch("duration"),
      fullName: watch("fullName"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      nationality: watch("nationality"),
      title: title,
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      dateOfBirth: watch("dateOfBirth"),
      withQR: watch("withQR"),
    };
    const joinedSelectedCourse = joinSelectedCourse(watchedFields.degree, watchedFields.selectedCourse);
    setDataForGeneration((prev) => ({
      ...prev,
      course: joinedSelectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      need_qr: !!watchedFields.withQR,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      invoiceBody: splitHtmlByBlocks(invoiceContent),
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      course: joinedSelectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      invoiceBody: splitHtmlByBlocksWithFontConvert(invoiceContent),
    }));
    const hasContent = content.trim().length > 0 || invoiceContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleInvoiceEditorChange = (newInvoiceContent: string) => setInvoiceContent(newInvoiceContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.invoiceBody}
                    inputProps={{
                    register: register("whose_doc"),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                    onArrowClick: whoseDocAuto.onArrowClick,
                  }}
                  identifier={"whose"}
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
                inputProps={{
                register: register("nationality"),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
                onArrowClick: nationalitiesAuto.onArrowClick,
              }}
              identifier={"nationality"}
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                  inputProps={{
                  register: register("course"),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  onArrowClick: offeredCourseAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="degree">Degree</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <AOPDegreeSelect control={control} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse")} />
              </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction")}
              />
              </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration")} />
              </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input invoiceBody">
              <AutocompleteInput
                label="Invoice details"
                value={invoiceBodyAuto.query}
                onChange={invoiceBodyAuto.onChange}
                onSelect={(v) => {
                  invoiceBodyAuto.onSelect(v);
                  setValue("invoiceBody", v);
                }}
                isOpen={invoiceBodyAuto.isOpen}
                options={invoiceBodyAuto.filtered}
                error={!!errors.invoiceBody}
                  inputProps={{
                  register: register("invoiceBody"),
                  close: invoiceBodyAuto.close,
                  setOpen: invoiceBodyAuto.onClick,
                  onArrowClick: invoiceBodyAuto.onArrowClick,
                }}
                identifier={"invoice"}
              />
            </div>
          </div>
          <label>Invoice body</label>
          <Editor
            value={invoiceContent}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={handleInvoiceEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Fedorova Anzhelika"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                  inputProps={{
                  register: register("signature"),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                  onArrowClick: signatureAuto.onArrowClick,
                }}
                identifier={"signature"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <AOPTaskIdInput
              register={register}
              setError={setError}
              clearErrors={clearErrors}
              applyTaskPrefill={applyTaskPrefill}
              lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
              onCopy={() => handleCopy("task_id")}
            />
            {country !== "poland" && (
              <div className="form-group-fields-invoice">
                <Controller
                  name="withQR"
                  control={control}
                  render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
